import { useEffect, useState } from 'react';
import HavasLogoSm from '../assets/HavasLogoSm';

interface AIResponseProps {
  responseText: string;
}

/**
 * Converts markdown-style formatting to HTML
 * - **text** → <strong>text</strong>
 * - *text* → <em>text</em>
 * - Preserves newlines
 */
const formatText = (text: string): React.ReactNode[] => {
  // Split on double newlines to create paragraphs
  const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
  
  return paragraphs.map((paragraph, paraIndex) => {
    // Split on single newlines within paragraphs
    const lines = paragraph.split('\n');
    
    return (
      <p key={paraIndex} className={paraIndex > 0 ? 'mt-4' : ''}>
        {lines.map((line, lineIndex) => {
          // Process markdown formatting
          // Strategy: Process bold first, then italic on remaining text
          let processedLine = line;
          const parts: React.ReactNode[] = [];
          let key = 0;
          
          // First, replace **bold** with placeholders
          const boldRegex = /\*\*(.+?)\*\*/g;
          const boldPlaceholders: Array<{ placeholder: string; text: string }> = [];
          let boldIndex = 0;
          
          processedLine = processedLine.replace(boldRegex, (match, text) => {
            const placeholder = `__BOLD_${boldIndex++}__`;
            boldPlaceholders.push({ placeholder, text });
            return placeholder;
          });
          
          // Then, replace *italic* with placeholders (but not placeholders)
          const italicRegex = /\*([^*]+?)\*/g;
          const italicPlaceholders: Array<{ placeholder: string; text: string }> = [];
          let italicIndex = 0;
          
          processedLine = processedLine.replace(italicRegex, (match, text) => {
            // Skip if this looks like a bold placeholder
            if (match.includes('__BOLD_')) return match;
            const placeholder = `__ITALIC_${italicIndex++}__`;
            italicPlaceholders.push({ placeholder, text });
            return placeholder;
          });
          
          // Now split by placeholders and reconstruct
          const allPlaceholders = [
            ...boldPlaceholders.map(p => ({ ...p, type: 'bold' as const })),
            ...italicPlaceholders.map(p => ({ ...p, type: 'italic' as const })),
          ];
          
          let lastIndex = 0;
          const placeholderRegex = /__(BOLD|ITALIC)_\d+__/g;
          let match;
          
          while ((match = placeholderRegex.exec(processedLine)) !== null) {
            // Add text before placeholder
            if (match.index > lastIndex) {
              parts.push(
                <span key={key++}>
                  {processedLine.substring(lastIndex, match.index)}
                </span>
              );
            }
            
            // Find and add the formatted text
            const placeholder = match[0];
            const placeholderData = allPlaceholders.find(p => p.placeholder === placeholder);
            if (placeholderData) {
              if (placeholderData.type === 'bold') {
                parts.push(<strong key={key++}>{placeholderData.text}</strong>);
              } else {
                parts.push(<em key={key++}>{placeholderData.text}</em>);
              }
            }
            
            lastIndex = match.index + match[0].length;
          }
          
          // Add remaining text
          if (lastIndex < processedLine.length) {
            parts.push(
              <span key={key++}>
                {processedLine.substring(lastIndex)}
              </span>
            );
          }
          
          // If no formatting found, just return the original line
          if (parts.length === 0) {
            parts.push(<span key={key++}>{line}</span>);
          }
          
          return (
            <span key={lineIndex}>
              {parts}
              {lineIndex < lines.length - 1 && <br />}
            </span>
          );
        })}
      </p>
    );
  });
};

export default function AIResponse({ responseText }: AIResponseProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Ensure initial state is rendered first, then start animation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          setIsVisible(true);
        }, 200);
      });
    });
  }, []);

  const logoStyle: React.CSSProperties = {
    transition: 'transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
    transform: isVisible ? 'scale(1)' : 'scale(0)',
    transformOrigin: 'center',
  };

  const formattedContent = formatText(responseText);

  return (
    <div className='ai-response mt-2 mb-6 flex items-start mr-2 lg:mr-0'>
      <div 
        className='pt-2 pr-2 flex-shrink-0 flex items-center justify-center -mt-0.5' 
        style={logoStyle}
      >
        <HavasLogoSm />
      </div>
      <div className='body-sm max-w-3xl pr-2'>
        {formattedContent}
      </div>
    </div>
  );
}
